import { useId } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAddPaymentFollowUpRemark } from '~/api/invoices';
import { Button, Dialog } from '~/components';
import { Field, Form, Textarea } from '~/components/Form';
import { isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

type Props = {
  invoiceId: number;
  onClose: () => void;
  paymentFollowUpId: number;
};

export const AddFollowUpRemarkDialog = (props: Props) => {
  return (
    <Dialog.Body>
      <AddFollowUpRemarkDialogContent {...props} />
    </Dialog.Body>
  );
};

const AddFollowUpRemarkDialogContent = ({ invoiceId, onClose, paymentFollowUpId }: Props) => {
  const mutation = useAddPaymentFollowUpRemark(invoiceId, paymentFollowUpId);

  const { control, handleSubmit } = useForm<{ remark: string }>({ defaultValues: { remark: '' } });
  const formId = useId();

  const { t } = useTranslation(['common', 'invoices', 'validation']);

  const onSubmit = handleSubmit(({ remark }) => {
    mutation.mutate(
      { remark },
      {
        onSuccess: () => onClose(),
        onError: () => toast.danger({ description: t('common:error') }),
      },
    );
  });

  return (
    <>
      <Dialog.Header
        description={t('invoices:details.followUp.modal.remarkMessage')}
        title={t('invoices:details.followUp.modal.titleRemark')}
      />

      <Form id={formId} onSubmit={onSubmit}>
        <Controller
          control={control}
          name="remark"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
            <Field.Root invalid={invalid} name={name}>
              <Field.Label srOnly>{t('invoices:details.followUp.modal.titleRemark')}</Field.Label>

              <Textarea
                autoFocus
                name={name}
                onBlur={onBlur}
                onValueChange={onChange}
                placeholder={t('invoices:details.followUp.modal.input.placeholder')}
                ref={ref}
                value={value}
              />

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{
            required: { value: true, message: t('validation:required') },
            validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
          }}
        />
      </Form>

      <Dialog.Actions>
        <Button form={formId} loading={mutation.isPending} type="submit">
          {t('invoices:details.followUp.modal.addRemark')}
        </Button>
      </Dialog.Actions>
    </>
  );
};
